/* AutoSeo — one-liner SEO wiring for any public/dynamic page.
 *
 * Fetches `/api/public/site-content/{pageKey}` once, reads the `seo` block
 * (populated in backend site_content_defaults.py + admin-editable overrides)
 * and passes seo_title / seo_description / seo_keywords straight into
 * PageMeta so browser tabs, Google, and social preview cards all pick up
 * the admin's copy.
 *
 * Callers still supply fallbacks so a fresh install / API outage never
 * ships an empty <title>:
 *
 *   <AutoSeo pageKey="directory"
 *            fallbackTitle="Directory — Recipe Filer"
 *            fallbackDescription="Browse public cookbooks…" />
 *
 * Overrides (title / description / keywords / includeWebsiteSchema / …)
 * pass through to PageMeta so a specific page can still force its own copy.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";

// In-flight fetches are cached module-level so multiple mounts on the same
// page (e.g. StrictMode double-render) don't fire duplicate requests.
const CACHE = new Map();

async function loadSeo(pageKey) {
  if (CACHE.has(pageKey)) return CACHE.get(pageKey);
  const promise = api
    .get(`/public/site-content/${pageKey}`)
    .then((r) => (r.data?.content?.seo) || null)
    .catch(() => null);
  CACHE.set(pageKey, promise);
  return promise;
}

export default function AutoSeo({
  pageKey,
  fallbackTitle,
  fallbackDescription,
  fallbackKeywords,
  ...rest
}) {
  const [seo, setSeo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    loadSeo(pageKey).then((s) => { if (!cancelled) setSeo(s || {}); });
    return () => { cancelled = true; };
  }, [pageKey]);

  const title = seo?.seo_title || fallbackTitle;
  const description = seo?.seo_description || fallbackDescription;
  const keywords = seo?.seo_keywords || fallbackKeywords;

  return (
    <PageMeta
      title={title}
      description={description}
      keywords={keywords}
      {...rest}
    />
  );
}
